import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { getUserFriendlyMessage } from '../core/errors';
import { createProposalClient } from '../core/ProposalClient';

/** Debounce delay for batching selection API syncs */
const SELECTION_SYNC_DEBOUNCE_MS = 300;

interface UseDebouncedSelectionSyncResult {
  /**
   * Schedule a debounced sync of the ids `getIds()` returns when it fires.
   * `onError`, if provided, runs after the request fails (post-rollback) so the
   * caller can undo any optimistic bookkeeping it did before scheduling.
   */
  scheduleSync: (getIds: () => number[], onError?: () => void) => void;
  /** Ref mirroring the latest `selectedActionIds`, for rollback-on-error. */
  selectedIdsRef: RefObject<number[]>;
}

/**
 * Debounced sync of the proposal's selection state to the server, batching
 * rapid clicks into one request.
 */
export function useDebouncedSelectionSync(
  proposalId: number,
  orgId: number,
  selectedActionIds: number[],
  setSelection: (actionIds: number[]) => void,
  triggerRefresh: () => void
): UseDebouncedSelectionSyncResult {
  const proposalClient = useMemo(() => createProposalClient(orgId), [orgId]);

  // Refs for debounced sync
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncInFlightRef = useRef(false);
  const pendingSyncRef = useRef<number[] | null>(null);
  const pendingOnErrorRef = useRef<(() => void) | null>(null);
  // `onError` callbacks from schedules that coalesced into the still-pending
  // debounced fire. A later `scheduleSync` supersedes the earlier one's ids
  // (the latest selection wins) but must NOT drop its callback: e.g. the
  // `missing_entity` reconciliation schedules a retry callback, then passive
  // applicability schedules the same PATCH with none — if the coalesced PATCH
  // then fails, the reconciliation marker must still be cleared so a retry runs.
  // Accumulate them and invoke all on failure.
  const scheduledOnErrorsRef = useRef<Array<() => void>>([]);
  // Always points at the latest `syncSelectionToServer`. A pending flush that
  // runs in the `finally` of an in-flight request must use THIS, not the
  // closure that started the request: when a reused panel switches proposals
  // mid-flight, the old closure still targets the previous `proposalId`, so
  // flushing through it would PATCH the new proposal's selection to the wrong
  // proposal. Synced in a passive effect below.
  const syncFnRef = useRef<
    ((ids: number[], onError?: () => void) => void) | null
  >(null);
  const selectedIdsRef = useRef(selectedActionIds);
  useEffect(() => {
    selectedIdsRef.current = selectedActionIds;
  }, [selectedActionIds]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Discard queued debounced work when the panel switches proposals (or orgs).
  // A reused panel keeps this hook mounted across the switch, so these refs
  // persist. A schedule made for the previous proposal — its pending ids and,
  // critically, its accumulated reconciliation `onError` callbacks — would
  // otherwise coalesce into the NEXT proposal's PATCH. If that PATCH then failed,
  // the previous proposal's callback would clear the new proposal's
  // reconciliation marker, suppressing its retry and leaving approval stuck.
  // Drop everything keyed to the old identity; the new proposal re-schedules any
  // work it needs after this. The in-flight guard is deliberately NOT reset — a
  // request already on the wire targets its own proposal and must finish.
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      scheduledOnErrorsRef.current = [];
      pendingSyncRef.current = null;
      pendingOnErrorRef.current = null;
    };
  }, [proposalId, orgId]);

  // Sync selection to server (fire-and-forget).
  // We do NOT call onUpdate with the response because:
  // 1. Selection is already tracked locally in this context (instant UI)
  // 2. Feeding the response back would replace the entire proposal object,
  //    creating new action array references that defeat row memoization
  // 3. This caused 3 cascading re-renders per checkbox click
  const syncSelectionToServer = useCallback(
    async (ids: number[], onError?: () => void) => {
      if (syncInFlightRef.current) {
        // Queue the update instead of dropping it. The latest ids win, but the
        // onError callbacks must ACCUMULATE, not replace: if a reconciliation
        // schedule queues its retry callback and then a later callback-less
        // schedule coalesces behind the same in-flight PATCH, replacing would
        // drop the reconciliation callback — so a failed PATCH would never clear
        // `reconciledMissingEntityRef` and approval stays stuck at STALE_SELECTION.
        pendingSyncRef.current = ids;
        const previousOnError = pendingOnErrorRef.current;
        pendingOnErrorRef.current =
          previousOnError || onError
            ? () => {
                previousOnError?.();
                onError?.();
              }
            : null;
        return;
      }
      syncInFlightRef.current = true;
      try {
        await proposalClient.updateSelection(proposalId, ids);
      } catch (error: unknown) {
        console.warn('[ProposalSelection] Failed to update selection:', {
          proposalId,
          selectedIdsCount: ids.length,
          error: error instanceof Error ? error.message : String(error),
        });
        // Rollback on error - use current selection from ref to avoid stale closure
        setSelection(selectedIdsRef.current);

        // Let the caller undo any optimistic bookkeeping (e.g. a "handled"
        // marker) before the refresh below re-derives state and can retry.
        onError?.();

        // Show error toast with user-friendly message
        toast.error(getUserFriendlyMessage(error));
        // Trigger refresh to get latest state
        triggerRefresh();
      } finally {
        syncInFlightRef.current = false;
        // Process pending sync if one was queued. Dispatch through the ref to
        // the LATEST callback, never this closure: if the proposal changed
        // while the request was in flight, this closure still targets the old
        // proposalId, and the queued ids belong to the new proposal.
        if (pendingSyncRef.current !== null) {
          const pendingIds = pendingSyncRef.current;
          const pendingOnError = pendingOnErrorRef.current ?? undefined;
          pendingSyncRef.current = null;
          pendingOnErrorRef.current = null;
          syncFnRef.current?.(pendingIds, pendingOnError);
        }
      }
    },
    [proposalId, proposalClient, setSelection, triggerRefresh]
  );

  // Keep the ref pointing at the current-render callback so a pending flush in
  // the `finally` above always targets the proposal currently mounted.
  useEffect(() => {
    syncFnRef.current = syncSelectionToServer;
  }, [syncSelectionToServer]);

  // Schedule a debounced sync
  const scheduleSync = useCallback(
    (getIds: () => number[], onError?: () => void) => {
      // Retain every coalesced schedule's callback — a later schedule replaces
      // the pending ids but must not discard an earlier retry callback.
      if (onError) {
        scheduledOnErrorsRef.current.push(onError);
      }
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      syncTimeoutRef.current = setTimeout(() => {
        const onErrors = scheduledOnErrorsRef.current;
        scheduledOnErrorsRef.current = [];
        const combinedOnError =
          onErrors.length > 0
            ? () => {
                for (const cb of onErrors) {
                  cb();
                }
              }
            : undefined;
        syncSelectionToServer(getIds(), combinedOnError);
      }, SELECTION_SYNC_DEBOUNCE_MS);
    },
    [syncSelectionToServer]
  );

  return { scheduleSync, selectedIdsRef };
}
